/**
 * Utility module for mapping route paths to Google Analytics page types.
 * @module RouteToGAPageTypeMapping
 */

import snakeCase from 'lodash/snakeCase'

/**
 * Maps route path segments to standardized Google Analytics page and sub-page types.
 *
 * @param {string[]} pathArray - An array of route path segments.
 * @returns {Object} An object containing pageType and subPageType for Google Analytics tracking.
 *
 * @description
 * Determines the appropriate GA page type based on route segments:
 * - Routes containing 'my-account' → pageType: 'my_account', subPageType: snake_case of last segment
 * - Routes containing 'checkout' → pageType and subPageType: 'checkout'
 * - Routes containing 'legal' → pageType: 'legal', subPageType: snake_case of last segment
 * - Other routes → pageType and subPageType: 'genericPage'
 *
 * @example
 * // Returns { pageType: 'my_account', subPageType: 'orders' }
 * mapRouteToGAPageType(['my-account', 'orders'])
 *
 * @example
 * // Returns { pageType: 'checkout', subPageType: 'checkout' }
 * mapRouteToGAPageType(['checkout'])
 *
 * @example
 * // Returns { pageType: 'genericPage', subPageType: 'genericPage' }
 * mapRouteToGAPageType(['products'])
 */
export const mapRouteToGAPageType = (
  pathArray: Array<string>
): { pageType: string; subPageType: string } => {
  if (pathArray.includes('my-account')) {
    // Get the last route
    const subPage = pathArray[pathArray.length - 1]
    const subPageFormatted = snakeCase(subPage)

    return { pageType: 'my_account', subPageType: subPageFormatted }
  }
  if (pathArray.includes('legal')) {
    const subPage = pathArray[pathArray.length - 1]
    const subPageFormatted = snakeCase(subPage)
    return { pageType: 'legal', subPageType: subPageFormatted }
  }
  if (pathArray.includes('help')) {
    return { pageType: 'help', subPageType: 'help' }
  }
  // specifically check /track/orders path, after legal and help routes to avoid conflicts
  if (
    pathArray.includes('track') &&
    (pathArray.includes('orders') || pathArray.includes('returns'))
  ) {
    return { pageType: 'order', subPageType: 'order' }
  }
  if (pathArray.includes('checkout')) {
    return { pageType: 'checkout', subPageType: 'checkout' }
  }
  if (pathArray.includes('cart')) {
    return { pageType: 'cart', subPageType: 'cart' }
  }
  return { pageType: 'genericPage', subPageType: 'genericPage' }
}
